import { apiFetch } from './api';
import type { MinecraftContainer, ArtifactRelease } from '@/deploy/types';

export interface SyncSummary {
  ok: number;
  ko: number;
  errors: string[];
  ranAt: string;
}

export interface SyncResult {
  ok: boolean;
  detail: string;
  containerVersion: string | null;
}

export interface ContainerUpdateInput {
  displayName?: string;
  color?: string;
  managementMode?: string;
  dotnetVersion?: string;
  dockerImage?: string;
  /** true archive, false désarchive (permission cicd.deploy.prod, comme archive() et unarchive()). */
  archived?: boolean;
}

export interface SyncCooldown {
  allMs: number;
  perContainerMs: Record<string, number>;
}

export const containersService = {
  /** Containers actifs ; `includeArchived` ajoute les archivés (champ `archived` à true). */
  list: (projectId: string, opts?: { includeArchived?: boolean }) =>
    apiFetch<MinecraftContainer[]>(
      `/api/projects/${projectId}/containers${opts?.includeArchived ? '?includeArchived=true' : ''}`,
    ),
  get: (projectId: string, code: string) =>
    apiFetch<MinecraftContainer>(`/api/projects/${projectId}/containers/${code}`),
  update: (projectId: string, code: string, patch: ContainerUpdateInput) =>
    apiFetch<MinecraftContainer>(`/api/projects/${projectId}/containers/${code}`, {
      method: 'PATCH', json: patch,
    }),
  /** Archive le container (409 si une release est en cours). Réversible par unarchive(). */
  archive: (projectId: string, code: string) =>
    apiFetch<MinecraftContainer>(`/api/projects/${projectId}/containers/${code}/archive`, { method: 'POST' }),
  /** Désarchive le container : il revient dans la liste et dans le processus de release, tel qu'il était. */
  unarchive: (projectId: string, code: string) =>
    apiFetch<MinecraftContainer>(`/api/projects/${projectId}/containers/${code}/unarchive`, { method: 'POST' }),
  syncAll: (projectId: string) =>
    apiFetch<SyncSummary>(`/api/projects/${projectId}/containers/sync`, { method: 'POST' }),
  syncOne: (projectId: string, code: string) =>
    apiFetch<SyncResult>(`/api/projects/${projectId}/containers/${code}/sync`, { method: 'POST' }),
  checkArtifactUpdates: (projectId: string, code: string) =>
    apiFetch<ArtifactRelease[]>(`/api/projects/${projectId}/containers/${code}/artifacts/check-updates`, { method: 'POST' }),
  /** État du cooldown sync (en ms restants par container + global) */
  syncCooldown: (projectId: string) =>
    apiFetch<SyncCooldown>(`/api/projects/${projectId}/containers/sync-cooldown`),
};
